import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdOpenInNew } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppMultiSelect,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../../components/ui';
import {
  findBuById,
  findSiteById,
  sitesForBu,
} from '../../../../data/master/businessUnits';
import {
  CONSTITUTION_OPTIONS,
  LOCAL_PROFESSIONAL_OPTIONS,
  MAJOR_CATEGORY_OPTIONS,
  VENDOR_VERIFICATION_OPTIONS,
} from '../../../../data/master/vendors';
import { findVillageById } from '../../../../data/master/villages';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';
import {
  lookupVillages,
  lookupBusinessUnits,
  lookupSites,
} from '../../../../repositories/lookup.repository';
import { listVendors } from '../../../../repositories/vendor.repository';

import { VendorFormModal } from './VendorFormModal';

function statusBadgeProps(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected' || status === 'Blocked') return { status: 'error' };
  return { status: 'pending' };
}

const DEFAULT_VERIFICATION_FILTER = ['Verified', 'Pending'];

export default function VendorsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const formModal = useDisclosure();
  const [villages, setVillages] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVendor, setEditingVendor] = useState(null);
  const [search, setSearch] = useState('');
  const [constitutionFilter, setConstitutionFilter] = useState('');
  const [villageFilter, setVillageFilter] = useState('');
  const [buFilter, setBuFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(DEFAULT_VERIFICATION_FILTER);
  const [activeFilter, setActiveFilter] = useState('');
  const [localProfessionalFilter, setLocalProfessionalFilter] = useState('');
  const [majorCategoryFilter, setMajorCategoryFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vendorRes, villageRes, buRes, siteRes] = await Promise.all([
        listVendors({ pageSize: 200 }),
        lookupVillages(),
        lookupBusinessUnits(),
        lookupSites(),
      ]);
      setVendors(vendorRes.items || []);
      setVillages(villageRes || []);
      setBusinessUnits(buRes || []);
      setSites(siteRes || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load vendors',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const villageFilterOptions = useMemo(
    () =>
      [...villages]
        .sort((a, b) => a.villageName.localeCompare(b.villageName))
        .map((v) => ({
          value: v.villageId,
          label: v.villageName,
          description: `${v.talukaName} · ${v.pin}`,
        })),
    [villages],
  );

  const buFilterOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst || b.buId,
        })),
    [businessUnits],
  );

  const siteFilterOptions = useMemo(
    () =>
      (buFilter ? sitesForBu(sites, buFilter) : sites.filter((s) => s.active !== false)).map(
        (s) => ({
          value: s.siteId,
          label: s.siteName,
          description: s.projectOperation || s.siteId,
        }),
      ),
    [buFilter, sites],
  );

  const pendingCount = useMemo(
    () =>
      vendors.filter((v) => (v.verificationStatus || 'Pending') === 'Pending').length,
    [vendors],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vendors.filter((v) => {
      const matchQ =
        !q ||
        (v.tradeAgencyName || '').toLowerCase().includes(q) ||
        (v.vendorId || '').toLowerCase().includes(q) ||
        (v.gstNumber || '').toLowerCase().includes(q) ||
        (v.ownerFullName || '').toLowerCase().includes(q) ||
        (v.mobile || '').includes(q) ||
        (v.kronosNumber || '').toLowerCase().includes(q);
      const matchC = !constitutionFilter || v.constitution === constitutionFilter;
      const matchV = !villageFilter || v.villageId === villageFilter;
      const matchBu = !buFilter || v.buId === buFilter;
      const matchSite = !siteFilter || v.siteId === siteFilter;
      const verification = v.verificationStatus || 'Pending';
      const matchVerification =
        !statusFilter.length || statusFilter.includes(verification);
      const isActive = v.active !== false;
      const matchActive =
        !activeFilter ||
        (activeFilter === 'Active' && isActive) ||
        (activeFilter === 'Inactive' && !isActive);
      const matchLocal =
        !localProfessionalFilter || v.localProfessional === localProfessionalFilter;
      const matchMajor = !majorCategoryFilter || v.majorCategory === majorCategoryFilter;
      return (
        matchQ &&
        matchC &&
        matchV &&
        matchBu &&
        matchSite &&
        matchVerification &&
        matchActive &&
        matchLocal &&
        matchMajor
      );
    });
  }, [
    vendors,
    search,
    constitutionFilter,
    villageFilter,
    buFilter,
    siteFilter,
    statusFilter,
    activeFilter,
    localProfessionalFilter,
    majorCategoryFilter,
  ]);

  const { sorted, sort, toggleSort } = useTableSort(filtered, {
    vendorId: (v) => v.vendorId,
    tradeAgencyName: (v) => v.tradeAgencyName,
    gstNumber: (v) => v.gstNumber,
    owner: (v) => v.ownerFullName,
    village: (v) => findVillageById(villages, v.villageId)?.villageName || v.villageId,
    verificationStatus: (v) => v.verificationStatus || 'Pending',
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const openAdd = () => {
    setEditingVendor(null);
    formModal.onOpen();
  };

  const openEdit = (row) => {
    setEditingVendor(row);
    formModal.onOpen();
  };

  const clearFilters = () => {
    setSearch('');
    setConstitutionFilter('');
    setVillageFilter('');
    setBuFilter('');
    setSiteFilter('');
    setStatusFilter([...DEFAULT_VERIFICATION_FILTER]);
    setActiveFilter('');
    setLocalProfessionalFilter('');
    setMajorCategoryFilter('');
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Flex align="center" gap={3} wrap="wrap" mb={1}>
            <Heading size={{ base: 'lg', xl: 'xl' }}>Vendors</Heading>
            <StatusBadge status={pendingCount > 0 ? 'pending' : 'neutral'}>
              {pendingCount} pending
            </StatusBadge>
          </Flex>
          <Text color="text.secondary" mt={2} maxW="720px" fontSize={{ base: '14px', xl: '15px' }}>
            Vendor Master — BU, Site, Village, KYC & verification. Click a row for details.
          </Text>
        </Box>
        <Flex gap={2} wrap="wrap">
          <OutlineButton
            size="sm"
            leftIcon={<MdOpenInNew />}
            onClick={() => window.open('/vendor/register', '_blank', 'noopener,noreferrer')}
          >
            Vendor registration link
          </OutlineButton>
          <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
            Add vendor
          </PrimaryButton>
        </Flex>
      </Flex>

      <AppCard mb={4} overflow="visible" position="relative" zIndex={2}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 8 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Trade name / GST / ID / mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Business Unit"
            labelFontSize="13px"
            h="40px"
            placeholder="All BUs"
            value={buFilter}
            onChange={(v) => {
              setBuFilter(v ?? '');
              setSiteFilter('');
            }}
            options={[{ value: '', label: 'All BUs' }, ...buFilterOptions]}
            isSearchable
            searchPlaceholder="Search BU…"
          />
          <AppSelect
            label="Site"
            labelFontSize="13px"
            h="40px"
            placeholder="All sites"
            value={siteFilter}
            onChange={(v) => setSiteFilter(v ?? '')}
            options={[{ value: '', label: 'All sites' }, ...siteFilterOptions]}
            isSearchable
            searchPlaceholder="Search site…"
          />
          <AppSelect
            label="Village"
            labelFontSize="13px"
            h="40px"
            placeholder="All villages"
            value={villageFilter}
            onChange={(v) => setVillageFilter(v ?? '')}
            options={[{ value: '', label: 'All villages' }, ...villageFilterOptions]}
            isSearchable
            searchPlaceholder="Search village / PIN…"
          />
          <AppSelect
            label="Constitution"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={constitutionFilter}
            onChange={(v) => setConstitutionFilter(v ?? '')}
            options={[
              { value: '', label: 'All' },
              ...CONSTITUTION_OPTIONS.map((c) => ({ value: c, label: c })),
            ]}
          />
          <AppSelect
            label="LOCAL / PROFESSIONAL"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={localProfessionalFilter}
            onChange={(v) => setLocalProfessionalFilter(v ?? '')}
            options={[{ value: '', label: 'All' }, ...LOCAL_PROFESSIONAL_OPTIONS]}
          />
          <AppSelect
            label="Major Category"
            labelFontSize="13px"
            h="40px"
            placeholder="All"
            value={majorCategoryFilter}
            onChange={(v) => setMajorCategoryFilter(v ?? '')}
            options={[{ value: '', label: 'All' }, ...MAJOR_CATEGORY_OPTIONS]}
          />
          <AppMultiSelect
            label="Verification"
            labelFontSize="13px"
            h="40px"
            placeholder="All statuses"
            values={statusFilter}
            onChange={setStatusFilter}
            options={VENDOR_VERIFICATION_OPTIONS}
            minW="160px"
          />
          <AppSelect
            label="Active"
            labelFontSize="13px"
            h="40px"
            value={activeFilter}
            onChange={(v) => setActiveFilter(v ?? '')}
            options={[
              { value: '', label: 'All' },
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
          <OutlineButton size="sm" w="100%" h="40px" minH="40px" onClick={clearFilters}>
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader rows={7} columns={5} />
        ) : (
          <>
            <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
              <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
                {totalCount} vendor{totalCount === 1 ? '' : 's'}
              </Text>
            </Box>
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="bg.input">
                  <Tr>
                    <SortableTh columnKey="vendorId" sort={sort} onSort={toggleSort} fontSize={{ base: '12px', xl: '13px' }}>
                      Vendor ID
                    </SortableTh>
                    <SortableTh columnKey="tradeAgencyName" sort={sort} onSort={toggleSort} fontSize={{ base: '12px', xl: '13px' }}>
                      Trade / Agency
                    </SortableTh>
                    <SortableTh columnKey="gstNumber" sort={sort} onSort={toggleSort} fontSize={{ base: '12px', xl: '13px' }}>
                      GST
                    </SortableTh>
                    <SortableTh columnKey="owner" sort={sort} onSort={toggleSort} fontSize={{ base: '12px', xl: '13px' }}>
                      Owner
                    </SortableTh>
                    <SortableTh columnKey="village" sort={sort} onSort={toggleSort} fontSize={{ base: '12px', xl: '13px' }}>
                      Village
                    </SortableTh>
                    <SortableTh columnKey="verificationStatus" sort={sort} onSort={toggleSort} fontSize={{ base: '12px', xl: '13px' }}>
                      Verification
                    </SortableTh>
                    <Th fontSize={{ base: '12px', xl: '13px' }}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageItems.map((row) => {
                    const v = findVillageById(villages, row.villageId);
                    const bu = findBuById(businessUnits, row.buId);
                    const site = findSiteById(sites, row.siteId);
                    const buSiteLabel = [bu?.shortName || row.buId, site?.siteName || row.siteId]
                      .filter(Boolean)
                      .join(' · ');
                    const status = row.verificationStatus || 'Pending';
                    return (
                      <Tr
                        key={row.id || row.vendorId}
                        cursor="pointer"
                        _hover={{ bg: 'bg.input' }}
                        onClick={() => navigate(`/admin/equipments/vendors/${row.vendorId}`)}
                      >
                        <Td fontFamily="mono" fontSize={{ base: '13px', xl: '14px' }}>
                          {row.vendorId}
                        </Td>
                        <Td>
                          <Text fontWeight="600" fontSize={{ base: '14px', xl: '15px' }}>
                            {row.tradeAgencyName}
                          </Text>
                          <Text fontSize="12px" color="text.secondary" mt={0.5}>
                            Kronos:{' '}
                            <Text as="span" fontFamily="mono" color="text.primary" fontWeight="500">
                              {row.kronosNumber || '—'}
                            </Text>
                          </Text>
                          {buSiteLabel ? (
                            <Text fontSize="12px" color="text.secondary" mt={0.5}>
                              {buSiteLabel}
                            </Text>
                          ) : null}
                          {row.localProfessional || row.majorCategory ? (
                            <Text fontSize="12px" color="text.secondary" mt={0.5}>
                              {[row.localProfessional, row.majorCategory].filter(Boolean).join(' · ')}
                            </Text>
                          ) : null}
                        </Td>
                        <Td fontSize={{ base: '13px', xl: '14px' }}>{row.gstNumber || '—'}</Td>
                        <Td fontSize={{ base: '14px', xl: '15px' }}>
                          {row.ownerFullName || '—'}
                        </Td>
                        <Td>
                          <Text fontSize={{ base: '13px', xl: '14px' }} fontWeight="600">
                            {v?.villageName || '—'}
                          </Text>
                          {v ? (
                            <Text fontSize="12px" color="text.secondary">
                              {v.villageId} · {row.pin || v.pin}
                            </Text>
                          ) : null}
                        </Td>
                        <Td>
                          <StatusBadge {...statusBadgeProps(status)}>{status}</StatusBadge>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            aria-label={`Edit ${row.tradeAgencyName}`}
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghostInk"
                            onClick={() => openEdit(row)}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                  {pageItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <Text color="text.secondary" py={8} textAlign="center" fontSize={{ base: '14px', xl: '15px' }}>
                          No vendors match your filters.
                        </Text>
                      </Td>
                    </Tr>
                  ) : null}
                </Tbody>
              </Table>
            </Box>
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </AppCard>

      <VendorFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        vendor={editingVendor}
        villages={villages}
        businessUnits={businessUnits}
        sites={sites}
        onSaved={load}
      />
    </Box>
  );
}
